import {
    Breakpoints,
    Fade,
    Text,
    TextTag,
    TokenTextAppearance,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import styled, {useTheme} from 'styled-components'
import React, {useEffect, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {RootState} from '../rootReducer'
import {Model} from '../../types/Model'
import isEmpty from 'lodash.isempty'
import {useTranslation} from 'react-i18next'

import {ModelCard} from '../components/ModelCard'
import {Faq} from '../components/Faq'
import {Global} from '../../shared/variables'
import {SingleModelView} from './SingleModelView'
import {SingleSimulation} from '../../types/SingleSimulation'
import {
    numberFormat,
    showSimulationByAdvance,
    showSimulationByMonthlyRange,
    usePrevious,
} from '../../shared/utilities'
import {TitleContainer, TitleWrapper, VisuallyHidden} from '../../shared/styled'
import {
    eventInfoCommon,
    TrackingAction,
    trackingData,
    trackingEvent,
} from '../../shared/trackingServiceHelper'

const CardsContainer = styled.ul`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: ${(props) => props.theme.size.dynamic0200};
    list-style: none;
    margin: 0;
    padding: 0;

    @media (min-width: ${Breakpoints.b560}px) {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: ${(props) => props.theme.size.dynamic0200};
    }

    @media (min-width: ${Breakpoints.b960}px) {
        grid-template-columns: 1fr 1fr 1fr;
        grid-gap: ${(props) => props.theme.size.dynamic0130};
    }
`

const CardListItem = styled.li`
    margin: 0;
    padding: 0;
`

export function Results(): React.JSX.Element {
    const {t} = useTranslation('fa-flotte')
    const theme = useTheme()

    const serviceType = useSelector(
        (state: RootState) => state.general.serviceType
    )

    const isPrivate = serviceType === Global.PRIVATE

    const modelsByPromotions = useSelector(
        (state: RootState) => state.configurationModelsData.modelsByPromotions
    )

    const filterValues = useSelector((state: RootState) => state.filterValues)
    const lowerBound = useSelector(
        (state: RootState) => state.filterValues.monthlyRange[0]
    )
    const upperBound = useSelector(
        (state: RootState) => state.filterValues.monthlyRange[1]
    )
    const advanceValue = useSelector(
        (state: RootState) => state.filterValues.advance
    )

    const filteredKm = useSelector(
        (state: RootState) => state.filterValues.totKm
    )

    const kmValues = filteredKm.map(
        (km: number) => `${numberFormat(km)} ${t('simulation.km')}`
    )

    const filteredPeriod = useSelector(
        (state: RootState) => state.filterValues.period
    )

    const periodValues = filteredPeriod.map(
        (period: number) => `${period} ${t('simulation.months')}`
    )

    const modelsValues = useSelector(
        (state: RootState) => state.filterValues.models
    )
    const fuelsValues = useSelector(
        (state: RootState) => state.filterValues.fuels
    )

    const simulationsList = useSelector(
        (state: RootState) => state.configurationModelsData.simulationsList
    )

    const filteredData = useMemo(
        () =>
            modelsByPromotions
                .map((model: Model) => {
                    if (!model.data?.simulations) {
                        return null
                    }
                    const filteredSimulations = model.data.simulations.filter(
                        (simulation: SingleSimulation) => {
                            const modelIncluded =
                                isEmpty(filterValues.models) ||
                                filterValues.models.includes(simulation.model)
                            const fuelIncluded =
                                isEmpty(filterValues.fuels) ||
                                filterValues.fuels.includes(simulation.fuel)
                            const periodIncluded =
                                isEmpty(filterValues.period) ||
                                filterValues.period.includes(
                                    simulation.num_of_months
                                )
                            const totKmIncluded =
                                isEmpty(filterValues.totKm) ||
                                filterValues.totKm.includes(simulation.tot_km)

                            const advanceIncluded =
                                showSimulationByAdvance(simulation)

                            const monthlyRangeIncluded =
                                showSimulationByMonthlyRange(simulation)

                            return (
                                modelIncluded &&
                                fuelIncluded &&
                                periodIncluded &&
                                totKmIncluded &&
                                advanceIncluded &&
                                monthlyRangeIncluded
                            )
                        }
                    )

                    if (filteredSimulations.length === 0) {
                        return null
                    }

                    return {
                        ...model,
                        data: {...model.data, simulations: filteredSimulations},
                    }
                })
                .filter(Boolean) as Model[],
        [modelsByPromotions, filterValues]
    )

    const prevFilterValues = usePrevious(filterValues)
    const resultsCount =
        isEmpty(simulationsList) || isEmpty(filteredData)
            ? Global.ZERO
            : filteredData.length
    const resultsTitle =
        resultsCount === Global.ONE
            ? t('results.title.oneResult')
            : t('results.title.multipleResults')

    useEffect(() => {
        const filtersSelected = Object.values(filterValues).some((value) =>
            Array.isArray(value)
                ? value.length > 0
                : value !== undefined && value !== null
        )
        const haveFiltersChanged =
            JSON.stringify(prevFilterValues) !== JSON.stringify(filterValues)

        if (!filtersSelected || !haveFiltersChanged) {
            return
        }

        const filterList = [
            {
                type: trackingData.RANGE,
                name: 'MB',
                lowerbound: lowerBound,
                upperbound: upperBound,
            },
            {
                type: trackingData.MULTI_VALUE,
                name: 'IP',
                values: [
                    ...(advanceValue.includes(Global.NO_ADVANCE)
                        ? [t('filters.advance.options.withoutAdvance')]
                        : []),
                    ...(advanceValue.includes(Global.ADVANCE)
                        ? [t('filters.advance.options.withAdvance')]
                        : []),
                ],
            },
            {
                type: trackingData.MULTI_VALUE,
                name: 'KR',
                values: isPrivate ? kmValues : kmValues,
            },
            {
                type: trackingData.MULTI_VALUE,
                name: 'DR',
                values: periodValues,
            },
            {type: trackingData.MULTI_VALUE, name: 'CG', values: modelsValues},
            {type: trackingData.MULTI_VALUE, name: 'GT', values: fuelsValues},
        ]

        trackingEvent(TrackingAction.VW_BASIC_FILTER_SEARCH_RESULT_LOAD, {
            eventInfo: {...eventInfoCommon},
            filter: {
                numberOfResults: filteredData.length,
                filterlist: filterList,
            },
        })
    }, [
        filterValues,
        lowerBound,
        upperBound,
        advanceValue,
        kmValues,
        periodValues,
        modelsValues,
        fuelsValues,
        filteredData,
        eventInfoCommon,
        prevFilterValues,
    ])

    return (
        <>
            <TitleContainer $dividerColor={theme.colors.content.primary}>
                <TitleWrapper as="h2">
                    {resultsCount === Global.ONE ? (
                        <>
                            <Text
                                tag={TextTag.span}
                                appearance={TokenTextAppearance.headline300}
                                color={TokenTextColor.primary}
                                bold
                            >
                                {String(Global.ONE)}
                            </Text>
                            <Text
                                tag={TextTag.span}
                                appearance={TokenTextAppearance.headline300}
                                color={TokenTextColor.primary}
                            >
                                {' '}
                                {resultsTitle}
                            </Text>
                        </>
                    ) : (
                        <>
                            <Text
                                tag={TextTag.span}
                                appearance={TokenTextAppearance.headline300}
                                color={TokenTextColor.primary}
                                bold
                            >
                                {String(resultsCount)}
                            </Text>
                            <Text
                                tag={TextTag.span}
                                appearance={TokenTextAppearance.headline300}
                                color={TokenTextColor.primary}
                            >
                                {' '}
                                {resultsTitle}
                            </Text>
                        </>
                    )}
                </TitleWrapper>
                <VisuallyHidden
                    role="status"
                    aria-live="polite"
                    aria-atomic={true}
                >
                    {`${resultsCount} ${resultsTitle}`}
                </VisuallyHidden>
            </TitleContainer>

            {!filteredData.length ? (
                <Text
                    tag={TextTag.span}
                    appearance={TokenTextAppearance.copy200}
                    color={TokenTextColor.primary}
                >
                    {t('results.noResults')}
                </Text>
            ) : (
                <Fade duration={300}>
                    {filteredData.length === Global.ONE ? (
                        filteredData.map((model: Model, index: number) => (
                            <SingleModelView
                                key={index}
                                modelData={model}
                            ></SingleModelView>
                        ))
                    ) : (
                        <>
                            <CardsContainer>
                                {!isEmpty(filteredData)
                                    ? filteredData.map(
                                          (model: Model, index: number) => (
                                              <CardListItem key={index}>
                                                  <ModelCard
                                                      modelData={model}
                                                  ></ModelCard>
                                              </CardListItem>
                                          )
                                      )
                                    : undefined}
                            </CardsContainer>
                        </>
                    )}
                </Fade>
            )}

            {isPrivate && <Faq></Faq>}
        </>
    )
}
